import React, { useState, useEffect } from 'react';
import { CheckCircleIcon, XCircleIcon, ClockIcon } from '@heroicons/react/24/outline';

const SystemStatus = () => {
  const [backendStatus, setBackendStatus] = useState('checking');
  const [backendMessage, setBackendMessage] = useState('');

  useEffect(() => {
    checkBackendStatus();
  }, []);

  const checkBackendStatus = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/test/ping`);
      const data = await response.json();
      
      if (data.success) {
        setBackendStatus('online');
        setBackendMessage(data.message);
      } else {
        setBackendStatus('error');
        setBackendMessage('Backend responded with error');
      }
    } catch (error) {
      setBackendStatus('offline');
      setBackendMessage('Cannot connect to backend');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online':
        return <CheckCircleIcon className="h-6 w-6 text-green-500" />;
      case 'offline':
      case 'error':
        return <XCircleIcon className="h-6 w-6 text-red-500" />;
      default:
        return <ClockIcon className="h-6 w-6 text-yellow-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online':
        return 'text-green-600';
      case 'offline':
      case 'error':
        return 'text-red-600';
      default:
        return 'text-yellow-600';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">System Status</h1>
        <p className="mt-2 text-sm text-gray-700">
          Check the status of all system components
        </p>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Service Status
          </h3>
          
          <div className="space-y-4">
            {/* Frontend Status */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3" />
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Frontend Application</h4>
                  <p className="text-sm text-gray-500">React development server</p>
                </div>
              </div>
              <span className="text-green-600 font-medium">Online</span>
            </div>

            {/* Backend Status */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                {getStatusIcon(backendStatus)}
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-gray-900">Backend API</h4>
                  <p className="text-sm text-gray-500">{backendMessage}</p>
                </div>
              </div>
              <span className={`font-medium ${getStatusColor(backendStatus)}`}>
                {backendStatus.charAt(0).toUpperCase() + backendStatus.slice(1)}
              </span>
            </div>

            {/* Database Status */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <XCircleIcon className="h-6 w-6 text-red-500 mr-3" />
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Database</h4>
                  <p className="text-sm text-gray-500">PostgreSQL database connection</p>
                </div>
              </div>
              <span className="text-red-600 font-medium">Not Connected</span>
            </div>
          </div>

          <div className="mt-6">
            <button
              onClick={checkBackendStatus}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Refresh Status
            </button>
          </div>
        </div>
      </div>

      {/* Test Login Section */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Test Credentials
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Use these credentials to test the login functionality:
          </p>
          
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { role: 'Admin', username: 'admin', password: 'password123' },
              { role: 'Teacher', username: 'teacher', password: 'password123' },
              { role: 'Student', username: 'student', password: 'password123' },
              { role: 'Accountant', username: 'accountant', password: 'password123' },
              { role: 'Management', username: 'management', password: 'password123' },
            ].map((cred) => (
              <div key={cred.role} className="bg-gray-50 p-3 rounded-lg">
                <h4 className="font-medium text-gray-900">{cred.role}</h4>
                <p className="text-sm text-gray-600">Username: {cred.username}</p>
                <p className="text-sm text-gray-600">Password: {cred.password}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Environment Info */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Environment Information
          </h3>
          
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-gray-500">Frontend URL</dt>
              <dd className="mt-1 text-sm text-gray-900">http://localhost:3000</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Backend API URL</dt>
              <dd className="mt-1 text-sm text-gray-900">{process.env.REACT_APP_API_URL || 'http://localhost:5000'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Environment</dt>
              <dd className="mt-1 text-sm text-gray-900">{process.env.NODE_ENV || 'development'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">App Version</dt>
              <dd className="mt-1 text-sm text-gray-900">{process.env.REACT_APP_VERSION || '1.0.0'}</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
};

export default SystemStatus;